/**
 * What a card shows of a stored call (AIT-108): built from the call's own bytes, never from the agent's description of
 * them, and only ever as plain text. Shared by the card and the tests; it runs nothing, loads nothing and fetches nothing.
 * A body it cannot read says so, with its size and type: nothing here guesses at a preview.
 */
import type { ActionCall } from "./types";

export const MAX_ACTION_BYTES = 1024 * 1024;
/** How many calls one `pandas propose action @items.json` files as one batch. */
export const MAX_ACTION_ITEMS = 20;

export type ActionField = { key: string; text: string };
export type ActionAttachment = { name: string; type: string; bytes: number };
export type ActionEmail = {
  from: string; to: string; cc: string; bcc: string; replyTo: string; subject: string;
  /** Every other header that says something about where it goes or who it is from, as it stands: nothing is left unsaid. */
  headers: ActionField[];
  /** The plain text a mail app shows when there is no HTML part. */
  text: string;
  /** The HTML part as source, drawn as text: what most mail apps show instead, and it may say something else. */
  html: string;
  attachments: ActionAttachment[];
};

export type ActionView =
  | { kind: "empty" }
  | { kind: "fields"; fields: ActionField[] }
  | { kind: "text"; text: string }
  /** `fields` are the JSON's other top-level fields, sent beside the email (a thread id). */
  | { kind: "email"; email: ActionEmail; fields: ActionField[] }
  /** What it is not, in words. `fields` when it is JSON the card can list, though not the email the rule expected. */
  | { kind: "unreadable"; why: string; bytes: number; contentType: string | null; fields?: ActionField[] };

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/** Bytes as a string of one char each, the form MIME's encodings work on. */
const latin1 = (bytes: Uint8Array): string => {
  let out = "";
  for (let i = 0; i < bytes.length; i += 8192) out += String.fromCharCode(...bytes.subarray(i, i + 8192));
  return out;
};
const toBytes = (s: string): Uint8Array => Uint8Array.from(s, (c) => c.charCodeAt(0) & 255);

function fromBase64(text: string): Uint8Array {
  return toBytes(atob(text));
}

/** A number a JSON reader cannot hold exactly: what a card drew would not be what a tool that reads it whole gets. */
const inexactNumber = (token: string) => {
  const digits = token.replace(/[eE].*$/, "").replace(/[-.]/g, "").replace(/^0+/, "").replace(/0+$/, "");
  return digits.length > 15 || !Number.isFinite(Number(token));
};

/**
 * What in a JSON text a card could not show as it would go, or null: a key one object holds twice, since a tool may read
 * either, or a number too long to read back exactly. Run only on text `JSON.parse` accepted.
 */
export function jsonProblem(text: string): string | null {
  const open: (Set<string> | null)[] = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (c === "-" || (c >= "0" && c <= "9")) {
      const token = /-?[0-9]+(?:\.[0-9]+)?(?:[eE][-+]?[0-9]+)?/y;
      token.lastIndex = i;
      const number = token.exec(text)?.[0] ?? c;
      if (inexactNumber(number)) return `the number ${number.slice(0, 40)} is too long to show exactly`;
      i += number.length - 1;
    } else if (c === '"') {
      let end = i + 1;
      while (end < text.length && text[end] !== '"') end += text[end] === "\\" ? 2 : 1;
      const token = text.slice(i, end + 1);
      i = end;
      let next = end + 1;
      while (" \t\r\n".includes(text[next] ?? "x")) next++;
      const keys = open.at(-1);
      if (keys && text[next] === ":") {
        const key = JSON.parse(token) as string;
        if (keys.has(key)) return `it holds the field '${key}' twice, and a tool may read either one`;
        keys.add(key);
      }
    } else if (c === "{") open.push(new Set());
    else if (c === "[") open.push(null);
    else if (c === "}" || c === "]") open.pop();
  }
  return null;
}

const fieldsOf = (o: Record<string, unknown>, skip: string[] = []): ActionField[] =>
  Object.entries(o).filter(([k]) => !skip.includes(k)).map(([key, value]) => ({ key, text: typeof value === "string" ? value : JSON.stringify(value, null, 2) }));

// ---------------------------------------------------------------- MIME, as far as a card needs it

type Part = { headers: [string, string][]; body: string };

/** Headers and body of one entity, in the bytes-as-chars form. Null when its first lines are not headers. */
function entity(raw: string): Part | null {
  const blank = /\r?\n\r?\n/.exec(raw);
  const head = blank ? raw.slice(0, blank.index) : raw;
  const body = blank ? raw.slice(blank.index + blank[0].length) : "";
  const headers: [string, string][] = [];
  for (const line of head.split(/\r?\n/)) {
    if (/^[ \t]/.test(line) && headers.length) headers[headers.length - 1]![1] += ` ${line.trim()}`;
    else {
      const m = /^([!-9;-~]+):[ \t]*(.*)$/.exec(line);
      if (!m) return line === "" ? { headers, body } : null;
      headers.push([m[1]!.toLowerCase(), m[2]!]);
    }
  }
  return { headers, body };
}

const headerValues = (p: Part, name: string) => p.headers.filter(([k]) => k === name).map(([, v]) => v);

/** `value; a=b; c="d"`, with RFC 2231's `name*=utf-8''…` read as `name`. */
function structured(value: string): { value: string; params: Record<string, string> } {
  const [first = "", ...rest] = value.match(/(?:[^;"]|"(?:[^"\\]|\\.)*")+/g) ?? [];
  const params: Record<string, string> = {};
  for (const piece of rest) {
    const m = /^\s*([^=\s]+)\s*=\s*(.*?)\s*$/.exec(piece);
    if (!m) continue;
    const key = m[1]!.toLowerCase();
    const said = m[2]!.replace(/^"(.*)"$/, "$1").replace(/\\(.)/g, "$1");
    if (key.endsWith("*")) {
      const encoded = /^([^']*)'[^']*'(.*)$/.exec(said);
      try {
        params[key.slice(0, -1)] = encoded ? decodeURIComponent(encoded[2]!) : said;
      } catch {
        params[key.slice(0, -1)] = said;
      }
    } else params[key] ??= said;
  }
  return { value: first.trim().toLowerCase(), params };
}

function textOf(bytes: Uint8Array, charset?: string): string {
  try {
    return new TextDecoder(charset || "utf-8", { ignoreBOM: true }).decode(bytes);
  } catch {
    return new TextDecoder("utf-8", { ignoreBOM: true }).decode(bytes);
  }
}

function quotedPrintable(body: string): Uint8Array {
  return toBytes(body.replace(/=\r?\n/g, "").replace(/=([0-9a-f]{2})/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16))));
}

/** A header's RFC 2047 words read, joined as the standard says when only space stands between two of them. */
function headerText(value: string): string {
  return value
    .replace(/(\?=)\s+(=\?)/g, "$1$2")
    .replace(/=\?([^?]+)\?([bq])\?([^?]*)\?=/gi, (whole, charset: string, enc: string, data: string) => {
      try {
        const bytes = enc.toLowerCase() === "b" ? fromBase64(data) : quotedPrintable(data.replace(/_/g, " "));
        return textOf(bytes, charset);
      } catch {
        return whole;
      }
    })
    .trim();
}

/** A part's content after its transfer encoding. Null when the encoding it claims cannot be read. */
function content(p: Part): Uint8Array | null {
  const encoding = (headerValues(p, "content-transfer-encoding")[0] ?? "").trim().toLowerCase();
  try {
    if (encoding === "base64") return fromBase64(p.body.replace(/\s+/g, ""));
    if (encoding === "quoted-printable") return quotedPrintable(p.body);
    return ["", "7bit", "8bit", "binary"].includes(encoding) ? toBytes(p.body) : null;
  } catch {
    return null;
  }
}

const MAX_DEPTH = 10;
const MAX_PARTS = 200;
/** What says how a part reads. Two of one and a mail app may follow either, so the card could not say which it drew. */
const ONE_ONLY = ["content-type", "content-transfer-encoding", "content-disposition"];
/** Headers with nothing to do with where a message goes: read by the card already, or only a mail app's bookkeeping. */
const SAID = new Set(["from", "to", "cc", "bcc", "reply-to", "subject", "date", "message-id", "mime-version", "in-reply-to", "references", ...ONE_ONLY]);

/** One leaf of the message: its text into the email, or what else it is by name and size. False for what it cannot read. */
function leaf(p: Part, email: ActionEmail, type: { value: string; params: Record<string, string> }): boolean {
  const disposition = structured(headerValues(p, "content-disposition")[0] ?? "");
  const named = disposition.params.filename ?? type.params.name;
  const bytes = content(p);
  if (!bytes) return false;
  const attached = disposition.value === "attachment";
  const isText = type.value === "text/plain" || type.value === "text/html";
  if (isText && !attached) {
    let decoded: string;
    try {
      decoded = new TextDecoder(type.params.charset || "utf-8", { ignoreBOM: true }).decode(bytes);
    } catch {
      return false;
    }
    if (type.value === "text/plain") email.text += (email.text ? "\n\n" : "") + decoded;
    else email.html += (email.html ? "\n\n" : "") + decoded;
    return true;
  }
  // Something with no name that is not text and not marked as an attachment: no way to say what a mail app makes of it.
  if (!attached && !named) return false;
  email.attachments.push({ name: headerText(named ?? "(no name)"), type: type.value, bytes: bytes.length });
  return true;
}

/**
 * Walks one entity into the email. Strict on purpose: anything it would have to skip or guess at is unreadable rather
 * than quietly left off the card. That means text outside a multipart's delimiters, a multipart that never closes, and
 * two of a header that says how a part reads.
 */
function walk(p: Part, email: ActionEmail, depth: number, count: { parts: number }): boolean {
  if (depth > MAX_DEPTH || ++count.parts > MAX_PARTS) return false;
  if (ONE_ONLY.some((h) => headerValues(p, h).length > 1)) return false;
  const type = structured(headerValues(p, "content-type")[0] ?? "text/plain");
  if (!type.value.startsWith("multipart/")) return leaf(p, email, type);
  const boundary = type.params.boundary;
  if (!boundary) return false;
  const delimiter = new RegExp(`(?:^|\\r?\\n)--${boundary.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&")}(--)?[ \\t]*(?=\\r?\\n|$)`, "g");
  let from = -1;
  let last = 0;
  let closed = false;
  for (const m of p.body.matchAll(delimiter)) {
    const piece = p.body.slice(last, m.index);
    if (from < 0 && piece.trim()) return false;
    if (from >= 0) {
      const inner = entity(piece.replace(/^\r?\n/, ""));
      if (!inner || !walk(inner, email, depth + 1, count)) return false;
    }
    from = m.index;
    last = m.index + m[0].length;
    if (m[1]) {
      closed = true;
      break;
    }
  }
  return closed && !p.body.slice(last).trim();
}

/** The email one MIME message is, or why not. */
function parseEmail(raw: string): ActionEmail | string {
  const top = entity(raw);
  if (!top) return "its first lines are not an email's headers";
  const all = (name: string) => headerValues(top, name).map(headerText).join(", ");
  const email: ActionEmail = {
    from: all("from"), to: all("to"), cc: all("cc"), bcc: all("bcc"), replyTo: all("reply-to"),
    subject: headerValues(top, "subject").map(headerText).join(" / "), text: "", html: "", attachments: [],
    headers: top.headers.filter(([k]) => !SAID.has(k) && !k.startsWith("x-")).map(([key, value]) => ({ key, text: headerText(value) })),
  };
  if (!email.to && !email.cc && !email.bcc) return "it names no recipient";
  return walk(top, email, 0, { parts: 0 }) ? email : "its parts could not be read";
}

/** The MIME message a JSON body holds, base64url in `raw` (or `message.raw`, a draft's shape), as bytes-as-chars. */
function rawMime(parsed: Record<string, unknown>): { raw: string; skip: string } | null {
  const nested = isObject(parsed.message) ? parsed.message.raw : undefined;
  // A call naming a draft by its id sends that draft, whatever else it holds, and with two emails in it a tool picks one:
  // either way what would go is not what the card could draw.
  if (parsed.id !== undefined || (parsed.raw !== undefined && parsed.message !== undefined)) return null;
  const value = typeof parsed.raw === "string" ? parsed.raw : typeof nested === "string" ? nested : null;
  if (value === null) return null;
  const standard = value.replace(/-/g, "+").replace(/_/g, "/").replace(/\s+/g, "");
  return { raw: atob(standard + "=".repeat((4 - (standard.length % 4)) % 4)), skip: typeof parsed.raw === "string" ? "raw" : "message" };
}

/** The card's reading of a stored call. Never throws: anything it cannot read is the `unreadable` view. */
export function actionView(call: Pick<ActionCall, "display" | "contentType" | "bodyBase64" | "bytes">): ActionView {
  if (!call.bodyBase64) return { kind: "empty" };
  const unreadable = (why: string, fields?: ActionField[]): ActionView => ({ kind: "unreadable", why, bytes: call.bytes, contentType: call.contentType, ...(fields ? { fields } : {}) });
  let bytes: Uint8Array;
  try {
    bytes = fromBase64(call.bodyBase64);
  } catch {
    return unreadable("its stored bytes could not be read");
  }
  let text: string | null = null;
  try {
    text = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    text = null;
  }
  let parsed: unknown;
  try {
    parsed = text === null ? undefined : JSON.parse(text);
  } catch {
    parsed = undefined;
  }
  if (call.display === "email-raw") {
    const mime = (call.contentType ?? "").split(";")[0]!.trim().toLowerCase() === "message/rfc822" ? { raw: latin1(bytes), skip: "" } : null;
    let found = mime;
    try {
      found ??= isObject(parsed) ? rawMime(parsed) : null;
    } catch {
      return unreadable("this should be an email, but its raw field is not base64url");
    }
    if (!found) return unreadable("this should be an email, but the call holds none to show", isObject(parsed) ? fieldsOf(parsed) : undefined);
    const email = parseEmail(found.raw);
    if (typeof email === "string") return unreadable(`this should be an email, but ${email}`);
    return { kind: "email", email, fields: isObject(parsed) ? fieldsOf(parsed, [found.skip]) : [] };
  }
  if (isObject(parsed)) return { kind: "fields", fields: fieldsOf(parsed) };
  if (parsed !== undefined) return { kind: "text", text: JSON.stringify(parsed, null, 2) };
  if (text !== null) return { kind: "text", text };
  return unreadable("it is not text");
}
